import * as XLSX from "xlsx";

/**
 * Export stock list to Excel.
 * @param {Array} stocks - Array of stock objects from the API.
 * @param {string} filename - Filename without extension.
 */
export const exportStocksToExcel = (stocks = [], filename = "stocks") => {
  if (!stocks?.length) {
    alert("No stock data to export");
    return;
  }

  const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
  };

  const rows = stocks.map((s) => {
    const quantity = toNumber(s.quantity ?? s.availableQuantity);
    const reserved = toNumber(s.reservedQuantity);
    const available = toNumber(s.availableQuantity ?? s.quantity) - reserved;

    const isLow =
      s.lowStockAlert === true || available <= toNumber(s.reorderLevel);

    return {
      "Product Name": s.productName || "",
      ItemCode: s.sku || s.barcode || "",
      Barcode: s.barcode || "",
      Branch: s.branchName || "",
      Category: s.categoryName || "",
      Unit: s.unitName || "",
      Quantity: quantity,
      Reserved: reserved,
      Available: available,
      "Reorder Level":
        s.reorderLevel == null || s.reorderLevel === ""
          ? ""
          : toNumber(s.reorderLevel),
      "Avg Cost": toNumber(s.averageCostPrice),
      "Last Purchase Price": toNumber(s.lastPurchasePrice),
      Status: isLow ? "Low Stock" : "In Stock",
      Active: s.active ? "Yes" : "No",
    };
  });

  const worksheet = XLSX.utils.json_to_sheet(rows);
  const headers = Object.keys(rows[0]);
  const range = XLSX.utils.decode_range(worksheet["!ref"]);

  const setFormula = (cellAddress, formula, cachedValue) => {
    worksheet[cellAddress] = {
      t: "n",
      f: formula,
      v: cachedValue,
      z: "#,##0.00",
    };
  };

  // Excel rows start at 1; row 1 contains column headers.
  // Available = Quantity - Reserved.
  const quantityCol = XLSX.utils.encode_col(headers.indexOf("Quantity"));
  const reservedCol = XLSX.utils.encode_col(headers.indexOf("Reserved"));
  const availableCol = XLSX.utils.encode_col(headers.indexOf("Available"));

  for (let row = 2; row <= range.e.r + 1; row++) {
    const address = `${availableCol}${row}`;
    const quantity = toNumber(rows[row - 2].Quantity);
    const reserved = toNumber(rows[row - 2].Reserved);

    setFormula(
      address,
      `${quantityCol}${row}-${reservedCol}${row}`,
      quantity - reserved
    );
  }

  // Apply number formats to numeric columns.
  const formats = {
    Quantity: "#,##0.###",
    Reserved: "#,##0.###",
    Available: "#,##0.###",
    "Reorder Level": "#,##0.###",
    "Avg Cost": "#,##0.00",
    "Last Purchase Price": "#,##0.00",
  };

  Object.entries(formats).forEach(([column, format]) => {
    const colIndex = headers.indexOf(column);

    for (let row = 1; row <= range.e.r; row++) {
      const address = XLSX.utils.encode_cell({
        r: row,
        c: colIndex,
      });

      const cell = worksheet[address];

      if (cell && cell.f == null && cell.t === "n") {
        cell.z = format;
      }
    }
  });

  worksheet["!cols"] = [
    { wch: 45 },
    { wch: 18 },
    { wch: 18 },
    { wch: 15 },
    { wch: 15 },
    { wch: 10 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 14 },
    { wch: 12 },
    { wch: 18 },
    { wch: 12 },
    { wch: 8 },
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Stock");

  XLSX.writeFile(
    workbook,
    `${filename}_${new Date().toISOString().slice(0, 10)}.xlsx`
  );
};
